import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { User } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";

export function useAuth() {
  const { data: user, isLoading, error, isError } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    retry: false,
    // Keep auth result around so login → home transition isn't wiped by gcTime: 0 races
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: true,
  });
  
  // Debug Logging
  if (isLoading) console.log('🔄 [useAuth] Loading...');
  if (isError) console.error('❌ [useAuth] Error State:', error);
  if (user === null) console.log('🛑 [useAuth] User is Explicitly Null (401)');

  return {
    user,
    // Treat error as "Not Loading anymore, just failed" -> isAuthenticated = false
    isLoading: isLoading && !isError, 
    isAuthenticated: !!user,
  };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<User>) => {
      const res = await apiRequest("PATCH", "/api/user/profile", data);
      return res.json();
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["/api/auth/user"], updatedUser);
    },
  });
}
